import React, { useState, useEffect } from 'react';
import { Button } from 'components/ui';
import { BuildingOfficeIcon, BookOpenIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { getCoursesOptions, getFeeByCourseId } from 'services/integrationService';
import { FilterDropdown, HierarchicalFilterDropdown } from 'components/leads/filters';
import { useMemo } from 'react';

export const AcademicFeeForm = ({ onNext, onCancel, paymentFormData, setPaymentFormData, isInternational = false, feeBreakup, setFeeBreakup }) => {
  const [college, setCollege] = useState(feeBreakup?.academic?.college || '');
  const [course, setCourse] = useState(feeBreakup?.academic?.course || '');
  const [optionsData, setOptionsData] = useState([]);
  const [feeDetails, setFeeDetails] = useState(feeBreakup?.academic?.feeDetails || null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingFee, setIsFetchingFee] = useState(false);
  const [isScholarshipApplied, setIsScholarshipApplied] = useState(feeBreakup?.academic?.isScholarshipApplied || false);

  const currencySymbol = isInternational ? '$' : '₹';
  const currencyLabel = isInternational ? 'USD' : 'INR';

  const fetchCourses = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const res = await getCoursesOptions();
      if (res.success && Array.isArray(res.data)) {
        setOptionsData(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch courses:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFeeDetails = async (courseId) => {
    if (!courseId) {
      setFeeDetails(null);
      return;
    }
    setIsFetchingFee(true);
    try {
      const res = await getFeeByCourseId(courseId, isInternational, isScholarshipApplied);
      if (res.success && res.data) {
        setFeeDetails(res.data);
      } else {
        setFeeDetails(null);
      }
    } catch (error) {
      console.error("Failed to fetch fee details:", error);
      setFeeDetails(null);
    } finally {
      setIsFetchingFee(false);
    }
  };
  const uniqueColleges = useMemo(() => {
    const uniqueCollegesMap = new Map();
    optionsData.forEach(item => {
      if (item.CollegeId && item.CollegeName) {
        const lowerId = String(item.CollegeId).toLowerCase();
        if (!uniqueCollegesMap.has(lowerId)) {
          uniqueCollegesMap.set(lowerId, item.CollegeName);
        }
      }
    });
    return Array.from(uniqueCollegesMap.entries());
  }, [optionsData]);


  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    // Wait for optionsData to load before syncing names to parent, 
    // unless we're initializing from an already populated feeBreakup
    const hasOptions = optionsData.length > 0;

     const selectedCollegeName = hasOptions 
    ? uniqueColleges.find(([id]) => id === String(college).toLowerCase())?.[1] || '' 
    : feeBreakup?.academic?.collegeName || '';

    let selectedCourseName = feeBreakup?.academic?.courseName || '';
    let courseCode = feeBreakup?.academic?.courseCode || '';
    let duration = feeBreakup?.academic?.duration || '';
    let semesters = feeBreakup?.academic?.semesters || '';
    let startDate = feeBreakup?.academic?.startDate || '';
    let endDate = feeBreakup?.academic?.endDate || '';
    if (hasOptions) {
      selectedCourseName = '';
      optionsData.forEach(item => {
        // GUID comparison should be case-insensitive to be safe
        const found = item.Courses?.find(c => String(c.Id).toLowerCase() === String(course).toLowerCase());
        if (found) {
          selectedCourseName = `${found.CourseName}${found.Abbreviation ? ` (${found.Abbreviation})` : ''}`;
          // The ERP API response confirms the field name is "CourseCode"
          let code = found.CourseCode || found.courseCode || found.AdmissionCode || found.admissionCode || found.Code || found.code || '';
          duration = found.Duration || found.duration || '';
          semesters = found.Semesters || found.semesters || '';
          startDate = found.StartDate || found.startDate || '';
          endDate = found.EndDate || found.endDate || '';
          if (code && isInternational) {
            code = `${code}-F`;
          }
          courseCode = code;
        }
      });
    }

    const currentCourseCode = courseCode;

    // Only update if we have a change OR if it's the initial load with data
    setFeeBreakup(prev => {
      // Avoid unnecessary updates if data hasn't changed to prevent render loops
      if (
        prev.academic.college === college &&
        prev.academic.course === course &&
        prev.academic.isScholarshipApplied === isScholarshipApplied &&
        prev.academic.collegeName === selectedCollegeName &&
        prev.academic.courseName === selectedCourseName &&
        prev.academic.courseCode === currentCourseCode &&
        prev.academic.duration === duration &&
        prev.academic.semesters === semesters &&
        prev.academic.startDate === startDate &&
        prev.academic.endDate === endDate &&
        prev.academic.feeDetails === feeDetails
      ) {
        return prev;
      }

      return {
        ...prev,
        academic: {
          ...prev.academic,
          college,
          course,
          isScholarshipApplied,
          amount: (() => {
            const ptuHead = feeDetails?.FeeDetails?.find(f => f.FeeHeadName?.toLowerCase().includes('ptu') || f.FeeHeadName?.toLowerCase().includes('counseling'));
            const ptuAmt = ptuHead ? ptuHead.FeeAmount : 0;
            const kitHead = feeDetails?.FeeDetails?.find(f => f.FeeHeadName?.toLowerCase().includes('kit'));
            const kitAmt = kitHead ? kitHead.FeeAmount : 0;
            return (feeDetails?.TotalAmount || 0) - ptuAmt - kitAmt;
          })(),
          collegeName: selectedCollegeName,
          courseName: selectedCourseName,
          courseCode: currentCourseCode,
          duration,
          semesters,
          startDate,
          endDate,
          feeDetails: feeDetails,
          actualBreakup: feeDetails?.FeeDetails || []
        }
      };
    });
  }, [college, course, isScholarshipApplied, isInternational, feeDetails, optionsData, uniqueColleges]);

  useEffect(() => {
    if (course) {
      fetchFeeDetails(course);
    } else {
      setFeeDetails(null);
    }
  }, [course, isInternational, isScholarshipApplied]);

  const handleNext = () => {
    const selectedCollegeName = uniqueColleges.find(([id]) => id === String(college).toLowerCase())?.[1] || '';
 
    let selectedCourseName = '';
    optionsData.forEach(item => {
      if (String(item.CollegeId).toLowerCase() === String(college).toLowerCase() || !college) {
        const found = item.Courses?.find(c => String(c.Id).toLowerCase() === String(course).toLowerCase());
        if (found) {
          selectedCourseName = `${found.CourseName}${found.Abbreviation ? ` (${found.Abbreviation})` : ''}`;
        }
      }
    });
 
    const ptuFeeHead = feeDetails?.FeeDetails?.find(f => f.FeeHeadName?.toLowerCase().includes('ptu') || f.FeeHeadName?.toLowerCase().includes('counseling'));
    const ptuFeeAmount = ptuFeeHead ? ptuFeeHead.FeeAmount : 0;
    
    const kitFeeHead = feeDetails?.FeeDetails?.find(f => f.FeeHeadName?.toLowerCase().includes('kit'));
    const kitFeeAmount = kitFeeHead ? kitFeeHead.FeeAmount : 0;

    const courseOnlyAmount = (feeDetails?.TotalAmount || 0) - ptuFeeAmount - kitFeeAmount;

    let courseCodeForNext = '';
    let durationForNext = '';
    let semestersForNext = '';
    let startDateForNext = '';
    let endDateForNext = '';
    optionsData.forEach(item => {
      if (String(item.CollegeId).toLowerCase() === String(college).toLowerCase() || !college) {
        const found = item.Courses?.find(c => String(c.Id).toLowerCase() === String(course).toLowerCase());
        if (found) {
          courseCodeForNext = found.CourseCode || found.courseCode || found.AdmissionCode || found.admissionCode || found.Code || found.code || '';
          durationForNext = found.Duration || found.duration || '';
          semestersForNext = found.Semesters || found.semesters || '';
          startDateForNext = found.StartDate || found.startDate || '';
          endDateForNext = found.EndDate || found.endDate || '';
          if (courseCodeForNext && isInternational) {
            courseCodeForNext = `${courseCodeForNext}-F`;
          }
        }
      }
    });

    const academicData = {
      college,
      course,
      isScholarshipApplied,
      amount: courseOnlyAmount,
      collegeName: selectedCollegeName,
      courseName: selectedCourseName,
      feeDetails: feeDetails,
      actualBreakup: feeDetails?.FeeDetails || [],
      feeBreakup: feeDetails?.FeeDetails || [],
      ptuFee: ptuFeeAmount,
      kitFee: kitFeeAmount,
      courseCode: courseCodeForNext,
      duration: durationForNext,
      semesters: semestersForNext,
      startDate: startDateForNext,
      endDate: endDateForNext
    };
    
    console.log("AcademicFeeForm Submission:", { 
      collegeId: college, 
      courseId: course, 
      collegeName: selectedCollegeName, 
      courseName: selectedCourseName, 
      courseCode: courseCodeForNext 
    });

    setFeeBreakup(prev => ({ ...prev, academic: academicData }));
    onNext(academicData);
  };

  // Get unique colleges dynamically
  
  // Prepare College Options for FilterDropdown
  const collegeOptions = useMemo(() => {
    return uniqueColleges.map(([id, name]) => ({
      id: String(id),
      value: String(id),
      label: name
    }));
  }, [uniqueColleges]);

  // Get courses based on selected college, or all courses if no college selected
  let availableCoursesData = optionsData;
  if (college) {
    availableCoursesData = optionsData.filter(d => String(d.CollegeId).toLowerCase() === String(college).toLowerCase());
  }

  // Prepare Course Options for FilterDropdown (Flat list)
  const courseOptions = useMemo(() => {
    const flatCourses = [];
    availableCoursesData.forEach(item => {
      (item.Courses || []).forEach(c => {
        flatCourses.push({
          id: String(c.Id),
          value: String(c.Id),
          label: `${c.CourseName}${c.Abbreviation ? ` (${c.Abbreviation})` : ''}`
        });
      });
    });
    return flatCourses;
  }, [availableCoursesData]);

  console.log("AcademicFeeForm Render:", { course, courseCode: paymentFormData.courseCode });
  return (
    <div className="space-y-6 animate-fade-in pb-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 dark:text-gray-100 dark:border-dark-700">
          Academic Fee Configuration
        </h3>
      </div>

      <div className="space-y-4">
        {/* College Selection */}
        <div className="relative">
          <FilterDropdown
            filterKey="college"
            label="Select College"
            options={collegeOptions}
            selectedValue={college}
            onSelect={(key, value) => {
              setCollege(value === 'all' ? '' : value);
              setCourse('');
            }}
            onClear={() => {
              setCollege('');
              setCourse('');
            }}
            placeholder="All Colleges"
            showSearch={true}
            searchThreshold={0}
            className="w-full"
            disabled={isLoading}
          />
        </div>

        {/* Course Selection */}
        <div className="relative">
          <FilterDropdown
            filterKey="course"
            label={
              <div className="flex items-center gap-1">
                <span>Select Course</span>
                <span className="text-red-500">*</span>
              </div>
            }
            options={courseOptions}
            selectedValue={course}
            onSelect={(key, value) => {
              if (value !== 'all') {
                setCourse(value);
                // If no college is selected, find the college for this course and select it
                if (!college) {
                  const item = optionsData.find(d => 
                    d.Courses?.some(c => String(c.Id).toLowerCase() === String(value).toLowerCase())
                  );
                  if (item) {
                    setCollege(String(item.CollegeId).toLowerCase());
                  }
                }
              }
            }}
            onClear={() => setCourse('')}
            placeholder="Choose a course..."
            showAllOption={false}
            showSearch={true}
            searchThreshold={0}
            className="w-full"
            disabled={isLoading}
          />
          {isLoading && (
            <div className="absolute right-10 top-9">
              <svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          )}
        </div>

        {/* Course Code Display */}
        {course && (paymentFormData.courseCode || feeBreakup?.academic?.courseCode) && (
          <div className="flex items-center gap-2 px-1 animate-fade-in">
            <span className="text-sm font-medium text-gray-500 dark:text-dark-400">Course Code:</span>
            <span className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-tight">
              {paymentFormData.courseCode || feeBreakup?.academic?.courseCode}
            </span>
          </div>
        )}

        {/* Scholarship Applied Checkbox */}
        {isInternational && course && (
          <div className="flex items-center space-x-2 py-1 px-1 transition-all">
            <input
              type="checkbox"
              id="scholarshipApplied"
              checked={isScholarshipApplied}
              onChange={(e) => setIsScholarshipApplied(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer dark:bg-dark-900 dark:border-dark-600"
            />
            <label
              htmlFor="scholarshipApplied"
              className="text-sm font-medium text-gray-700 dark:text-dark-200 cursor-pointer select-none"
            >
              Scholarship Applied?
            </label>
          </div>
        )}

        {isFetchingFee ? (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-6 w-6 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="ml-3 text-sm text-gray-500 dark:text-dark-400">Fetching fee details...</span>
          </div>
        ) : feeDetails && (
          <div className="space-y-3 animate-fade-in">
            <div className="rounded-xl border border-gray-200 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-800/50 overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900 flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-700 dark:text-dark-200 uppercase tracking-wider">Fee Breakup</span>
                <span className="text-xs font-medium px-2 py-1 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 rounded-full border border-primary-100 dark:border-primary-800">
                  {isInternational ? 'Annually' : 'Semester-wise'}
                </span>
              </div>
              <div className="p-4 space-y-3">
                {feeDetails.FeeDetails?.filter(head => head.FeeHeadName !== 'Examination Fee').map((head, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-dark-300">{head.FeeHeadName}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-100">{currencySymbol} {head.FeeAmount.toLocaleString()}</span>
                  </div>
                ))}
                <div className="pt-3 mt-2 border-t border-dashed border-gray-300 dark:border-dark-600 flex justify-between items-center">
                  <span className="text-sm font-bold text-gray-900 dark:text-dark-100 uppercase tracking-wide">Total Amount</span>
                  <span className="text-lg font-black text-primary-600 dark:text-primary-400">{currencyLabel} {feeDetails.TotalAmount.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="pt-4 flex justify-end gap-3 border-t border-gray-100 dark:border-dark-700">
        <Button variant="outlined" onClick={onCancel}>Back</Button>
        <Button
          color="primary"
          onClick={handleNext}
          disabled={!course || isFetchingFee || !feeDetails}
        >
          Next: Hostel Fee
        </Button>
      </div>
    </div>
  );
};
